import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import {
    FaUsers, FaSearch, FaEye, FaEyeSlash, FaFileExcel, FaFileImport,
    FaChevronLeft, FaChevronRight, FaUserPlus, FaEdit, FaLink, FaUnlink,
    FaTimes, FaSave, FaUserSlash, FaCheckCircle, FaExclamationTriangle,
    FaBriefcase, FaAddressCard, FaUniversity, FaIdCard, FaBuilding
} from 'react-icons/fa';
import './BaseFuncionarios.css';

const STATUS_LABEL = {
    ativo: 'Ativo', inativo: 'Inativo', afastado: 'Afastado', ferias: 'Férias', desligado: 'Desligado'
};

const CAMPOS_VAZIOS = {
    matricula: '', nome: '', cpf: '', rg: '', data_nascimento: '', estado_civil: '',
    cnh_numero: '', cnh_categoria: '', pis_pasep: '', email_pessoal: '', telefone_pessoal: '',
    endereco_completo: '', filiacao_mae: '', filiacao_pai: '', contato_emergencia: '',
    cargo: '', area: '', salario: '', data_admissao: '', tipo_contrato: '', sindicato: '', lider_id: '',
    banco_nome: '', banco_agencia: '', banco_conta: '', banco_tipo_conta: '', banco_chave_pix: '', banco_titular_doc: '',
    formacao: '', cursos_certificacoes: '', idiomas: '', historico_profissional: '', beneficios: '', equipamentos: '',
    observacoes: '', status: 'ativo'
};

// --- Modal de Cadastro/Edição — abas, ocupa boa parte da tela ---
const FuncionarioModal = ({ funcionario, funcionarios, onClose, onSaved }) => {
    const isEdicao = !!funcionario;
    const [aba, setAba] = useState('pessoal');
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(() => {
        if (!funcionario) return CAMPOS_VAZIOS;
        const arrayToText = (v) => Array.isArray(v) ? v.join('\n') : (v || '');
        return {
            ...CAMPOS_VAZIOS,
            ...funcionario,
            salario: funcionario.salario ?? '',
            formacao: arrayToText(funcionario.formacao),
            cursos_certificacoes: arrayToText(funcionario.cursos_certificacoes),
            idiomas: arrayToText(funcionario.idiomas),
            historico_profissional: arrayToText(funcionario.historico_profissional),
            beneficios: arrayToText(funcionario.beneficios),
            equipamentos: arrayToText(funcionario.equipamentos),
        };
    });

    const set = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.matricula.trim() || !form.nome.trim()) {
            toast.error('Matrícula e nome são obrigatórios.');
            setAba('pessoal');
            return;
        }
        setSaving(true);
        try {
            if (isEdicao) {
                const { data } = await api.put(`/api/rh/funcionarios/${funcionario.id}`, form);
                toast.success('Funcionário atualizado.');
                onSaved({ ...funcionario, ...data });
            } else {
                const { data } = await api.post('/api/rh/funcionarios', form);
                toast.success('Funcionário cadastrado.');
                onSaved({ ...data });
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao salvar funcionário.');
        } finally {
            setSaving(false);
        }
    };

    const abas = [
        { id: 'pessoal', label: 'Dados Pessoais', icon: <FaIdCard /> },
        { id: 'profissional', label: 'Profissional', icon: <FaBriefcase /> },
        { id: 'contato', label: 'Contato & Endereço', icon: <FaAddressCard /> },
        { id: 'bancario', label: 'Bancário', icon: <FaUniversity /> },
        { id: 'complementar', label: 'Formação & Extras', icon: <FaBuilding /> },
    ];

    const outrosFuncionarios = funcionarios.filter(f => f.id !== funcionario?.id);

    return (
        <div className="bf-modal-overlay" onClick={onClose}>
            <div className="bf-modal-wide" onClick={e => e.stopPropagation()}>
                <div className="bf-modal-header">
                    <h3>{isEdicao ? `Editar ${form.nome || 'funcionário'}` : 'Novo Funcionário'}</h3>
                    <button type="button" className="bf-modal-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="bf-modal-tabs">
                    {abas.map(a => (
                        <button key={a.id} type="button" className={`bf-modal-tab ${aba === a.id ? 'active' : ''}`} onClick={() => setAba(a.id)}>
                            {a.icon} {a.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="bf-modal-body">
                        {aba === 'pessoal' && (
                            <div className="bf-form-grid">
                                <label>Matrícula *<input value={form.matricula} onChange={set('matricula')} required /></label>
                                <label>Nome completo *<input value={form.nome} onChange={set('nome')} required /></label>
                                <label>CPF<input value={form.cpf} onChange={set('cpf')} /></label>
                                <label>RG<input value={form.rg} onChange={set('rg')} /></label>
                                <label>Data de nascimento<input type="date" value={form.data_nascimento} onChange={set('data_nascimento')} /></label>
                                <label>Estado civil<input value={form.estado_civil} onChange={set('estado_civil')} /></label>
                                <label>CNH — número<input value={form.cnh_numero} onChange={set('cnh_numero')} /></label>
                                <label>CNH — categoria<input value={form.cnh_categoria} onChange={set('cnh_categoria')} /></label>
                                <label>PIS/PASEP<input value={form.pis_pasep} onChange={set('pis_pasep')} /></label>
                                <label>Status
                                    <select value={form.status} onChange={set('status')}>
                                        {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                </label>
                            </div>
                        )}

                        {aba === 'profissional' && (
                            <div className="bf-form-grid">
                                <label>Cargo<input value={form.cargo} onChange={set('cargo')} /></label>
                                <label>Área<input value={form.area} onChange={set('area')} /></label>
                                <label>Salário base<input type="number" step="0.01" value={form.salario} onChange={set('salario')} /></label>
                                <label>Data de admissão<input type="date" value={form.data_admissao} onChange={set('data_admissao')} /></label>
                                <label>Tipo de contrato<input value={form.tipo_contrato} onChange={set('tipo_contrato')} placeholder="CLT, PJ, Estágio..." /></label>
                                <label>Sindicato<input value={form.sindicato} onChange={set('sindicato')} /></label>
                                <label>Líder / gestor direto
                                    <select value={form.lider_id || ''} onChange={set('lider_id')}>
                                        <option value="">Nenhum</option>
                                        {outrosFuncionarios.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                    </select>
                                </label>
                            </div>
                        )}

                        {aba === 'contato' && (
                            <div className="bf-form-grid">
                                <label>Email pessoal<input type="email" value={form.email_pessoal} onChange={set('email_pessoal')} /></label>
                                <label>Telefone pessoal<input value={form.telefone_pessoal} onChange={set('telefone_pessoal')} /></label>
                                <label className="bf-span-2">Endereço completo<input value={form.endereco_completo} onChange={set('endereco_completo')} /></label>
                                <label>Filiação — mãe<input value={form.filiacao_mae} onChange={set('filiacao_mae')} /></label>
                                <label>Filiação — pai<input value={form.filiacao_pai} onChange={set('filiacao_pai')} /></label>
                                <label className="bf-span-2">Contato de emergência<input value={form.contato_emergencia} onChange={set('contato_emergencia')} /></label>
                            </div>
                        )}

                        {aba === 'bancario' && (
                            <div className="bf-form-grid">
                                <label>Banco<input value={form.banco_nome} onChange={set('banco_nome')} /></label>
                                <label>Agência<input value={form.banco_agencia} onChange={set('banco_agencia')} /></label>
                                <label>Conta<input value={form.banco_conta} onChange={set('banco_conta')} /></label>
                                <label>Tipo de conta<input value={form.banco_tipo_conta} onChange={set('banco_tipo_conta')} placeholder="Corrente, Poupança..." /></label>
                                <label>Chave PIX<input value={form.banco_chave_pix} onChange={set('banco_chave_pix')} /></label>
                                <label>Documento do titular<input value={form.banco_titular_doc} onChange={set('banco_titular_doc')} /></label>
                            </div>
                        )}

                        {aba === 'complementar' && (
                            <div className="bf-form-grid">
                                <label className="bf-span-2">Formação (uma por linha)<textarea rows={3} value={form.formacao} onChange={set('formacao')} /></label>
                                <label className="bf-span-2">Cursos e certificações (um por linha)<textarea rows={3} value={form.cursos_certificacoes} onChange={set('cursos_certificacoes')} /></label>
                                <label>Idiomas (um por linha)<textarea rows={3} value={form.idiomas} onChange={set('idiomas')} /></label>
                                <label>Benefícios (um por linha)<textarea rows={3} value={form.beneficios} onChange={set('beneficios')} /></label>
                                <label>Equipamentos (um por linha)<textarea rows={3} value={form.equipamentos} onChange={set('equipamentos')} /></label>
                                <label>Histórico profissional (um por linha)<textarea rows={3} value={form.historico_profissional} onChange={set('historico_profissional')} /></label>
                                <label className="bf-span-2">Observações<textarea rows={2} value={form.observacoes} onChange={set('observacoes')} /></label>
                            </div>
                        )}
                    </div>

                    <div className="bf-modal-footer">
                        <button type="button" className="bf-btn-cancel" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="bf-btn-confirm" disabled={saving}>
                            <FaSave /> {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Modal de vínculo com usuário do sistema ---
const VincularUsuarioModal = ({ funcionario, onClose, onLinked }) => {
    const [modo, setModo] = useState('novo');
    const [userId, setUserId] = useState('');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = modo === 'existente' ? { user_id: userId } : { email, senha };
            const { data } = await api.post(`/api/rh/funcionarios/${funcionario.id}/vincular-usuario`, body);
            toast.success('Usuário vinculado com sucesso.');
            onLinked(data.user_id);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao vincular usuário.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bf-modal-overlay" onClick={onClose}>
            <div className="bf-modal-content" onClick={e => e.stopPropagation()}>
                <h3>Vincular acesso ao sistema</h3>
                <p className="bf-modal-subtitle">Funcionário: <strong>{funcionario.nome}</strong></p>
                <form onSubmit={handleSubmit}>
                    <div className="bf-toggle-group">
                        <button type="button" className={modo === 'novo' ? 'active' : ''} onClick={() => setModo('novo')}>Criar novo usuário</button>
                        <button type="button" className={modo === 'existente' ? 'active' : ''} onClick={() => setModo('existente')}>Vincular existente</button>
                    </div>
                    {modo === 'novo' ? (
                        <>
                            <input className="bf-modal-input" placeholder="Email de acesso" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                            <input className="bf-modal-input" placeholder="Senha provisória" type="password" value={senha} onChange={e => setSenha(e.target.value)} required />
                        </>
                    ) : (
                        <input className="bf-modal-input" placeholder="ID do usuário existente" value={userId} onChange={e => setUserId(e.target.value)} required />
                    )}
                    <div className="bf-modal-actions">
                        <button type="button" className="bf-btn-cancel" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="bf-btn-confirm" disabled={saving}>{saving ? 'Vinculando...' : 'Vincular'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Modal de importação em massa ---
const ImportarModal = ({ onClose, onImported }) => {
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [resultado, setResultado] = useState(null);
    const inputRef = useRef(null);

    const handleDownloadModelo = async () => {
        try {
            const response = await api.get('/api/rh/funcionarios/modelo', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'modelo_funcionarios.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            toast.error('Erro ao baixar o modelo.');
        }
    };

    const handleImportar = async () => {
        if (!file) return toast.warn('Selecione um arquivo primeiro.');
        setImporting(true);
        setResultado(null);
        try {
            const formData = new FormData();
            formData.append('planilha', file);
            const { data } = await api.post('/api/rh/funcionarios/importar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResultado(data);
            if (data.inseridos > 0 || data.atualizados > 0) onImported();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao importar planilha.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="bf-modal-overlay" onClick={onClose}>
            <div className="bf-modal-content" onClick={e => e.stopPropagation()}>
                <h3>Importar funcionários</h3>
                <p className="bf-modal-subtitle">Envie uma planilha .xlsx ou .csv. Funcionários com matrícula já existente são atualizados.</p>

                <button type="button" className="bf-button-secondary bf-full-width" onClick={handleDownloadModelo}>
                    <FaFileExcel /> Baixar planilha modelo
                </button>

                <div className="bf-dropzone" onClick={() => inputRef.current?.click()}>
                    <FaFileImport size={28} />
                    <span>{file ? file.name : 'Clique para selecionar o arquivo'}</span>
                    <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={e => setFile(e.target.files[0])} />
                </div>

                {resultado && (
                    <div className="bf-import-result">
                        <p><FaCheckCircle color="#10B981" /> {resultado.inseridos} novos, {resultado.atualizados} atualizados.</p>
                        {resultado.erros.length > 0 && (
                            <div className="bf-import-errors">
                                <p><FaExclamationTriangle color="#EF4444" /> {resultado.erros.length} linha(s) com erro:</p>
                                <ul>
                                    {resultado.erros.slice(0, 10).map((e, i) => <li key={i}>Linha {e.linha}: {e.motivo}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <div className="bf-modal-actions">
                    <button type="button" className="bf-btn-cancel" onClick={onClose}>Fechar</button>
                    <button type="button" className="bf-btn-confirm" onClick={handleImportar} disabled={importing}>
                        {importing ? 'Importando...' : 'Importar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default function BaseFuncionarios() {
    const [funcionarios, setFuncionarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('todos');
    const [showSalaries, setShowSalaries] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const [modalFuncionario, setModalFuncionario] = useState(undefined); // undefined = fechado, null = novo, obj = editar
    const [vinculoFuncionario, setVinculoFuncionario] = useState(null);
    const [isImportOpen, setIsImportOpen] = useState(false);

    const fetchFuncionarios = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/rh/funcionarios');
            setFuncionarios(Array.isArray(data) ? data : []);
        } catch {
            toast.error('Erro ao carregar a base de funcionários.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFuncionarios(); }, []);
    useEffect(() => { setCurrentPage(1); }, [search, statusFilter]);

    const filtered = useMemo(() => {
        return funcionarios
            .filter(f => statusFilter === 'todos' ? true : f.status === statusFilter)
            .filter(f => (f.nome?.toLowerCase().includes(search.toLowerCase()) || f.matricula?.toLowerCase().includes(search.toLowerCase()) || f.cargo?.toLowerCase().includes(search.toLowerCase())));
    }, [funcionarios, search, statusFilter]);

    const paginated = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filtered.slice(start, start + itemsPerPage);
    }, [filtered, currentPage]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

    const kpis = useMemo(() => {
        const hoje = new Date();
        const admissoesMes = funcionarios.filter(f => {
            if (!f.data_admissao) return false;
            const d = new Date(f.data_admissao);
            return d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear();
        }).length;
        return {
            total: funcionarios.length,
            ativos: funcionarios.filter(f => f.status === 'ativo').length,
            semAcesso: funcionarios.filter(f => !f.user_id).length,
            admissoesMes
        };
    }, [funcionarios]);

    const formatCurrency = (value) => {
        const num = parseFloat(value);
        if (!value || isNaN(num)) return '---';
        return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const formatDate = (value) => {
        if (!value) return '---';
        return new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    const handleSaved = (atualizado) => {
        setFuncionarios(prev => {
            const existe = prev.some(f => f.id === atualizado.id);
            return existe ? prev.map(f => f.id === atualizado.id ? { ...f, ...atualizado } : f) : [...prev, atualizado];
        });
        setModalFuncionario(undefined);
    };

    const handleAlterarStatus = async (func, novoStatus) => {
        const confirmMsg = novoStatus === 'desligado'
            ? `Confirma o desligamento de ${func.nome}?`
            : `Alterar status de ${func.nome} para "${STATUS_LABEL[novoStatus]}"?`;
        if (!window.confirm(confirmMsg)) return;
        try {
            await api.patch(`/api/rh/funcionarios/${func.id}/status`, { status: novoStatus });
            setFuncionarios(prev => prev.map(f => f.id === func.id ? { ...f, status: novoStatus } : f));
            toast.success('Status atualizado.');
        } catch {
            toast.error('Erro ao atualizar status.');
        }
    };

    return (
        <div className="base-funcionarios-wrapper">
            <div className="bf-header-row">
                <h1 className="bf-header"><FaUsers /> Base de Funcionários</h1>
                <div className="bf-header-actions">
                    <button className="bf-button-secondary" onClick={() => setIsImportOpen(true)}><FaFileImport /> Importar</button>
                    <button className="bf-btn-primary" onClick={() => setModalFuncionario(null)}><FaUserPlus /> Novo Funcionário</button>
                </div>
            </div>

            <div className="bf-kpi-row">
                <div className="bf-kpi-card"><span className="bf-kpi-value">{kpis.total}</span><span className="bf-kpi-label">Total de funcionários</span></div>
                <div className="bf-kpi-card"><span className="bf-kpi-value">{kpis.ativos}</span><span className="bf-kpi-label">Ativos</span></div>
                <div className="bf-kpi-card"><span className="bf-kpi-value">{kpis.semAcesso}</span><span className="bf-kpi-label">Sem acesso ao sistema</span></div>
                <div className="bf-kpi-card"><span className="bf-kpi-value">{kpis.admissoesMes}</span><span className="bf-kpi-label">Admissões no mês</span></div>
            </div>

            <div className="bf-control-panel">
                <div className="bf-search-bar">
                    <FaSearch className="bf-search-icon" />
                    <input type="text" placeholder="Buscar por nome, matrícula ou cargo..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="bf-filters-group">
                    <button className={`bf-filter-btn ${statusFilter === 'todos' ? 'active' : ''}`} onClick={() => setStatusFilter('todos')}>Todos</button>
                    <button className={`bf-filter-btn ${statusFilter === 'ativo' ? 'active' : ''}`} onClick={() => setStatusFilter('ativo')}>Ativos</button>
                    <button className={`bf-filter-btn ${statusFilter === 'inativo' ? 'active' : ''}`} onClick={() => setStatusFilter('inativo')}>Inativos</button>
                    <button className={`bf-filter-btn ${statusFilter === 'desligado' ? 'active' : ''}`} onClick={() => setStatusFilter('desligado')}>Desligados</button>
                </div>
            </div>

            <div className="bf-table-container">
                <div className="bf-table-wrapper">
                    <table className="bf-table">
                        <thead>
                            <tr>
                                <th>Funcionário</th>
                                <th>Cargo / Área</th>
                                <th>Contrato</th>
                                <th>Admissão</th>
                                <th>Acesso</th>
                                <th>Status</th>
                                <th>
                                    <div className="bf-th-salary">
                                        <span>Salário</span>
                                        <button onClick={() => setShowSalaries(!showSalaries)} className={`bf-btn-toggle-eye ${showSalaries ? 'active' : ''}`}>
                                            {showSalaries ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </th>
                                <th style={{ textAlign: 'right' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && paginated.map(func => (
                                <tr key={func.id}>
                                    <td>
                                        <div className="bf-user-info-text">
                                            <span>{func.nome}</span>
                                            <small className="bf-font-mono">Mat. {func.matricula}</small>
                                        </div>
                                    </td>
                                    <td>{func.cargo || '---'}{func.area ? ` · ${func.area}` : ''}</td>
                                    <td>{func.tipo_contrato || '---'}</td>
                                    <td>{formatDate(func.data_admissao)}</td>
                                    <td>
                                        {func.user_id
                                            ? <span className="bf-access-tag with-access"><FaLink /> Vinculado</span>
                                            : <button className="bf-access-tag no-access" onClick={() => setVinculoFuncionario(func)}><FaUnlink /> Vincular</button>}
                                    </td>
                                    <td><span className={`bf-status-tag bf-status-${func.status}`}>{STATUS_LABEL[func.status] || func.status}</span></td>
                                    <td>{showSalaries ? <span className="bf-salary-visible">{formatCurrency(func.salario)}</span> : <span className="bf-salary-hidden">••••••</span>}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div className="bf-action-buttons">
                                            <button className="bf-btn-action-edit" onClick={() => setModalFuncionario(func)} title="Editar"><FaEdit /> Editar</button>
                                            {func.status !== 'desligado' && (
                                                <button className="bf-btn-action-danger" onClick={() => handleAlterarStatus(func, 'desligado')} title="Desligar">
                                                    <FaUserSlash />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && paginated.length === 0 && (
                                <tr><td colSpan="8" className="bf-empty-row">Nenhum funcionário encontrado.</td></tr>
                            )}
                            {loading && <tr><td colSpan="8" className="bf-empty-row">Carregando...</td></tr>}
                        </tbody>
                    </table>
                </div>

                {!loading && totalPages > 1 && (
                    <div className="bf-pagination-container">
                        <span className="bf-pagination-info">Mostrando <strong>{paginated.length}</strong> de <strong>{filtered.length}</strong></span>
                        <div className="bf-pagination-controls">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="bf-pagination-btn"><FaChevronLeft /></button>
                            <span className="bf-pagination-current">Página {currentPage} de {totalPages}</span>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="bf-pagination-btn"><FaChevronRight /></button>
                        </div>
                    </div>
                )}
            </div>

            {modalFuncionario !== undefined && (
                <FuncionarioModal
                    funcionario={modalFuncionario}
                    funcionarios={funcionarios}
                    onClose={() => setModalFuncionario(undefined)}
                    onSaved={handleSaved}
                />
            )}

            {vinculoFuncionario && (
                <VincularUsuarioModal
                    funcionario={vinculoFuncionario}
                    onClose={() => setVinculoFuncionario(null)}
                    onLinked={(userId) => {
                        setFuncionarios(prev => prev.map(f => f.id === vinculoFuncionario.id ? { ...f, user_id: userId } : f));
                        setVinculoFuncionario(null);
                    }}
                />
            )}

            {isImportOpen && (
                <ImportarModal onClose={() => setIsImportOpen(false)} onImported={() => { setIsImportOpen(false); fetchFuncionarios(); }} />
            )}
        </div>
    );
}